import {
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
  ReloadOutlined,
  LoadingOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  CameraOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Form,
  Input,
  message,
  Pagination,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
} from "antd";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { CREATE_jwel1 } from "../../Config/Config";

const authOptions = ["administrator", "operator"];

const authTypeOptions = authOptions.map((type) => ({
  value: type,
  label: type.charAt(0).toUpperCase() + type.slice(1),
}));

const USER_MODULE_OPTIONS = [
  { value: "INVENTORY", label: "Inventory" },
  { value: "POINT OF SALE", label: "Point of Sale" },
];

const IMAGE_BASE = "https://image.timeserasoftware.in";
const MAX_PROFILE_PIC_MB = 5;

// Compresses/normalizes any selected image to a JPEG base64 string,
// same helper as used in UploadFile.jsx.
const toJpegBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", 0.9).split(",")[1]);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const buildProfilePicUrl = (firmName, uname) => {
  const firmSegment = encodeURIComponent(firmName || "");
  const nameSegment = encodeURIComponent(uname.trim().toUpperCase());
  return `${IMAGE_BASE}/${firmSegment}/PROFILE_${nameSegment}.jpg`;
};

const withCacheBust = (url, version) => {
  if (!url || !version) return url;
  return `${url}${url.includes("?") ? "&" : "?"}v=${encodeURIComponent(version)}`;
};

/* MUI field styled to match antd's default 32px input —
   removes the double border / strikethrough look and the oversized text */
const authFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    border: "none !important",
    borderRadius: "6px",
    backgroundColor: "#fff",
    fontSize: 14,
    minHeight: 32,
    padding: "0 9px !important",
    "& fieldset": {
      borderColor: "#d9d9d9 !important",
      borderWidth: "1px !important",
      borderRadius: "6px",
    },
    "&:hover fieldset": {
      borderColor: "#4096ff !important",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1677ff !important",
      borderWidth: "1px !important",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 2px rgba(5, 145, 255, 0.1)",
    },
  },
  "& .MuiInputBase-input": {
    padding: "4px 0 !important",
    height: "auto",
    fontSize: 14,
    lineHeight: 1.5714,
  },
  "& .MuiInputBase-input::placeholder": {
    color: "rgba(0,0,0,0.25)",
    opacity: 1,
  },
  "& .MuiAutocomplete-endAdornment": {
    right: "6px !important",
  },
};

const AuthTypeAutocomplete = ({
  value,
  onChange,
  onSelect,
  inputRef,
  onEnterKey,
}) => {
  const selected = authTypeOptions.find((o) => o.value === value) || null;

  return (
    <Autocomplete
      autoHighlight
      options={authTypeOptions}
      value={selected}
      getOptionLabel={(option) => option?.label ?? ""}
      isOptionEqualToValue={(option, val) => option.value === val?.value}
      onChange={(_, newValue) => {
        onChange?.(newValue?.value);
        onSelect?.(newValue?.value);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          size="small"
          placeholder="Select authenticate type"
          sx={authFieldSx}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onEnterKey?.();
            }
          }}
        />
      )}
    />
  );
};

const G = {
  container: {
    backgroundColor: "#f0f2f5",
    position: "relative",
    padding: "16px 20px",
    minHeight: "100vh-97px",
  },
  card: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 4px 24px rgba(15,36,96,0.13)",
    overflow: "hidden",
    width: "90%",
    maxWidth: 480,
    marginBottom: 24,
  },
  header: {
    background: "linear-gradient(90deg, #0f2460 0%, #1a3a8f 100%)",
    padding: "14px 18px",
    color: "#fff",
    fontWeight: 700,
    fontSize: 15,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  body: {
    padding: "20px",
  },
  formRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 14,
  },
  label: {
    width: 110,
    minWidth: 110,
    fontSize: 13,
    fontWeight: 600,
    color: "#111827",
  },
  actionBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 10,
  },
  tableCard: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 4px 24px rgba(15,36,96,0.13)",
    overflow: "hidden",
    width: "80%",
  },
  tableHeader: {
    padding: "8px 18px",
    fontWeight: 700,
    fontSize: 15,
    letterSpacing: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 16,
  },
  avatarPicker: {
    position: "relative",
    width: 84,
    height: 84,
    borderRadius: "50%",
    overflow: "hidden",
    cursor: "pointer",
    border: "2px solid #e5e9f5",
    background: "#f4f6fb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  avatarImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  avatarBadge: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: "#0f2460",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    border: "2px solid #fff",
  },
};

const UserAccounts = () => {
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState([]);
  const usernameRef = useRef(null);
  const mobileRef = useRef(null);
  const passwordRef = useRef(null);
  const authTypeRef = useRef(null);
  const userModuleRef = useRef(null);
  const profilePicInputRef = useRef(null);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [togglingKey, setTogglingKey] = useState(null);

  const [usernameStatus, setUsernameStatusState] = useState("idle");
  const usernameCheckTimeout = useRef(null);
  const usernameCheckId = useRef(0);

  const usernameStatusRef = useRef("idle");
  const setUsernameStatus = (value) => {
    usernameStatusRef.current = value;
    setUsernameStatusState(value);
  };

  const [profilePic, setProfilePic] = useState(null);
  const [existingProfilePicUrl, setExistingProfilePicUrl] = useState("");

  const tenantName = localStorage.getItem("tenantName");
  const username = localStorage.getItem("UName");

  const [ownerRecord, setOwnerRecord] = useState(null);
  const [resolvingOwner, setResolvingOwner] = useState(true);
  const resolvedTenantName = ownerRecord?.TENANTNAME || tenantName;

  useEffect(() => {
    if (!resolvingOwner && ownerRecord) {
      setTimeout(() => usernameRef.current?.focus(), 0);
    }
  }, [resolvingOwner, ownerRecord]);

  const filteredData = rawData.filter((item) => {
    const role = String(item.ROLE ?? "").toUpperCase();
    return (
      item.USERNAME?.toLowerCase().includes(searchText.toLowerCase()) &&
      role !== "ADMIN" &&
      role !== "ORIGIN"
    );
  });

  const paginatedData = filteredData.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  // Untouched — API integration here already confirmed working.
  const resolveOwnerRecord = useCallback(async () => {
    if (!tenantName) {
      message.error("No tenant context found — please log in again");
      setResolvingOwner(false);
      return null;
    }
    setResolvingOwner(true);
    try {
      const res = await axios.get(
        `${CREATE_jwel1}/api/Tenant/GetDataFromGivenTableNameWithWhere?tableName=ClientDetails&where=USERNAME='${username}'`,
        { headers: { tenantName: username } },
      );
      const data = Array.isArray(res.data)
        ? res.data
        : res.data
          ? [res.data]
          : [];
      const record =
        data.find((r) => r.ROLE?.toUpperCase().includes("ADMIN")) || data[0];
      if (!record) {
        message.error("Could not resolve firm details for this tenant");
        setOwnerRecord(null);
        return null;
      }
      setOwnerRecord(record);
      return record;
    } catch (error) {
      console.error("Error resolving firm details:", error);
      message.error("Could not resolve firm details for this tenant");
      setOwnerRecord(null);
      return null;
    } finally {
      setResolvingOwner(false);
    }
  }, [tenantName]);

  const fetchData = (tenant) => {
    setLoading(true);
    setPage(1);
    const where = encodeURIComponent(`tenantname='${tenant}'`);
    axios
      .get(
        `${CREATE_jwel1}/api/Tenant/GetDataFromGivenTableNameWithWhere?tableName=ClientDetails&where=${where}`,
        { headers: { tenantName } },
      )
      .then((res) =>
        setRawData(
          (res.data || []).map((item, idx) => ({ ...item, key: idx + 1 })),
        ),
      )
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    (async () => {
      const record = await resolveOwnerRecord();
      const tenant = record?.TENANTNAME || tenantName;
      if (tenant) fetchData(tenant);
    })();
  }, [resolveOwnerRecord]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    if (resolvedTenantName) fetchData(resolvedTenantName);
  };

  // ===== username availability check =====
  const checkUsernameAvailability = (value) => {
    if (usernameCheckTimeout.current)
      clearTimeout(usernameCheckTimeout.current);
    const trimmed = value?.trim();

    if (!trimmed) {
      usernameCheckId.current += 1;
      setUsernameStatus("idle");
      return;
    }
    if (editingKey && trimmed.toUpperCase() === editingKey.toUpperCase()) {
      usernameCheckId.current += 1;
      setUsernameStatus("available");
      return;
    }

    setUsernameStatus("checking");
    const requestId = ++usernameCheckId.current;

    usernameCheckTimeout.current = setTimeout(async () => {
      try {
        const where = encodeURIComponent(`USERNAME='${trimmed}'`);
        const res = await axios.get(
          `${CREATE_jwel1}/api/Tenant/GetDataFromGivenTableNameWithWhere?tableName=ClientDetails&where=${where}`,
          { headers: { tenantName } },
        );
        if (requestId !== usernameCheckId.current) return; // superseded by newer input
        const exists = Array.isArray(res.data)
          ? res.data.length > 0
          : !!res.data;
        setUsernameStatus(exists ? "taken" : "available");
      } catch (error) {
        if (requestId !== usernameCheckId.current) return;
        setUsernameStatus("idle"); // don't block on a network hiccup
      } finally {
        if (requestId === usernameCheckId.current) {
          // Force the Form.Item to re-evaluate now that the real
          // status is known, instead of waiting for the next keystroke.
          // validateUsername reads usernameStatusRef (updated above,
          // synchronously) rather than the possibly-stale state closure.
          form.validateFields(["username"]).catch(() => { });
        }
      }
    }, 450);
  };

  const validateUsername = (_, value) => {
    if (!value) return Promise.resolve();
    const status = usernameStatusRef.current;
    if (status === "checking") {
      return Promise.reject(new Error("Checking username availability…"));
    }
    if (status === "taken") {
      return Promise.reject(new Error("This username already exists"));
    }
    return Promise.resolve();
  };

  const usernameSuffix = (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 14,
      }}
    >
      {usernameStatus === "checking" && (
        <LoadingOutlined style={{ color: "#8a998f" }} />
      )}
      {usernameStatus === "taken" && (
        <CloseCircleFilled style={{ color: "#c9463c" }} />
      )}
      {usernameStatus === "available" && (
        <CheckCircleFilled style={{ color: "#189a4d" }} />
      )}
    </span>
  );

  const handleProfilePicSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      message.warning("Please select an image file");
      return;
    }
    if (file.size > MAX_PROFILE_PIC_MB * 1024 * 1024) {
      message.warning(
        `Image exceeds ${MAX_PROFILE_PIC_MB}MB — please choose a smaller file`,
      );
      return;
    }

    setProfilePic((prev) => {
      if (prev?.preview) URL.revokeObjectURL(prev.preview); // avoid leaking blob URLs
      return { file, preview: URL.createObjectURL(file) };
    });
  };

  const clearProfilePic = () => {
    setProfilePic((prev) => {
      if (prev?.preview) URL.revokeObjectURL(prev.preview);
      return null;
    });
    setExistingProfilePicUrl("");
  };

  const getProfilePicUrl = (record) => {
    const raw =
      record?.PROFILEPIC ??
      record?.ProfilePic ??
      record?.Profilepic ??
      record?.profilepic ??
      record?.PROFILE_PIC;
    return raw && raw !== "-" ? raw : "";
  };

  const handleSubmit = async (values) => {
    if (loading) return;

    if (!ownerRecord) {
      message.error(
        "Firm details not resolved yet — please wait and try again",
      );
      return;
    }

    setLoading(true);
    try {
      if (!editingKey) {
        const where = encodeURIComponent(
          `USERNAME='${values.username.trim()}'`,
        );
        const checkRes = await axios.get(
          `${CREATE_jwel1}/api/Tenant/GetDataFromGivenTableNameWithWhere?tableName=CLIENTDETAILS&where=${where}`,
          { headers: { tenantName } },
        );

        if (checkRes.data && checkRes.data.length > 0) {
          form.setFields([
            {
              name: "username",
              errors: ["Username already exists"],
            },
          ]);
          setLoading(false);
          return;
        }
      }

      let profilePicUrl = editingKey ? existingProfilePicUrl || "-" : "-";

      if (profilePic?.file) {
        try {
          const fileBase = await toJpegBase64(profilePic.file);
          const fileName = `PROFILE_${values.username.trim().toUpperCase()}.jpg`;

          const uploadRes = await axios.post(
            `${CREATE_jwel1}/api/Utilities/SaveClientImages`,
            {
              fileName,
              fileBase,
              clientName: ownerRecord.FIRMNAME,
              dbId: "",
            },
            { headers: { "Content-Type": "application/json", tenantName } },
          );

          if (uploadRes.status !== 200 || uploadRes.data?.success === false) {
            throw new Error("Upload endpoint returned a non-success result");
          }

          profilePicUrl = buildProfilePicUrl(
            ownerRecord.FIRMNAME,
            values.username,
          );
        } catch (picErr) {
          console.error("Profile picture upload failed:", picErr);
          message.error("Failed to upload profile picture");
          setLoading(false);
          return;
        }
      }

      const payload = {
        username: values.username.trim(),
        password: values.password,
        loginmobileno: values.mobile.trim(),
        otpcode: "",
        profilepic: profilePicUrl,
        printerip: ownerRecord.PRINTERIP || "",
        tenantname: ownerRecord.TENANTNAME || resolvedTenantName || "",
        gstper: ownerRecord.GSTPER ?? 0,
        firmname: ownerRecord.FIRMNAME || "",
        role: values.authType.toUpperCase(),
        startdate: new Date().toISOString(),
        enddate: new Date().toISOString(),
        apptype: ownerRecord.APPTYPE || "",
        deliverycharges: ownerRecord.DELIVERYCHARGES ?? "0",
        printertype: ownerRecord.PRINTERTYPE || "-",
        usermodule: values.userModule || "",
      };

      if (!editingKey) {
        await axios.post(
          `${CREATE_jwel1}/api/Tenant/InsertClientDetails`,
          payload,
          { headers: { tenantName } },
        );
        message.success("User created successfully");
      } else {
        const where = encodeURIComponent(`USERNAME='${editingKey}'`);
        await axios.post(
          `${CREATE_jwel1}/api/Tenant/DeleteDataFromGivenTableNameWithWhere?tableName=ClientDetails&where=${where}`,
          {},
          { headers: { tenantName } },
        );
        await axios.post(
          `${CREATE_jwel1}/api/Tenant/InsertClientDetails`,
          payload,
          { headers: { tenantName } },
        );
        message.success("User updated successfully");
      }

      form.resetFields();
      setEditingKey(null);
      setUsernameStatus("idle");
      clearProfilePic();
      fetchData(resolvedTenantName);
      setTimeout(() => usernameRef.current?.focus(), 0);
    } catch (error) {
      console.error(error);
      message.error(
        editingKey ? "Failed to update user" : "Failed to create user",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingKey(record.USERNAME);
    form.setFieldsValue({
      username: record.USERNAME,
      password: record.PASSWORD,
      mobile: record.LOGINMOBILENO || "",
      authType: record.ROLE?.toLowerCase(),
      userModule:
        record.ROLE?.toLowerCase() === "administrator"
          ? "ALL"
          : record.USERMODULE || undefined,
    });
    setUsernameStatus("available"); // it's their own existing username
    setProfilePic(null);
    setExistingProfilePicUrl(getProfilePicUrl(record));
    setTimeout(() => usernameRef.current?.focus(), 0);
  };

  const handleDelete = async (record) => {
    try {
      setLoading(true);
      const where = encodeURIComponent(`USERNAME='${record.USERNAME}'`);
      await axios.post(
        `${CREATE_jwel1}/api/Tenant/DeleteDataFromGivenTableNameWithWhere?tableName=ClientDetails&where=${where}`,
        {},
        {
          headers: {
            tenantName,
          },
        },
      );
      message.success("User deleted successfully");
      fetchData(resolvedTenantName);
      if (editingKey === record.USERNAME) {
        handleCancel();
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = useCallback(() => {
    form.resetFields();
    setEditingKey(null);
    setUsernameStatus("idle");
    clearProfilePic();
    setTimeout(() => usernameRef.current?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const handleStatusToggle = async (record, checked) => {
    const rowKey = record.key;
    const previousValue = record.DELIVERYCHARGES;
    const newValue = checked ? "1" : "0";

    setTogglingKey(rowKey);
    setRawData((prev) =>
      prev.map((r) =>
        r.key === rowKey ? { ...r, DELIVERYCHARGES: newValue } : r,
      ),
    );

    try {
      await axios.post(
        `${CREATE_jwel1}/api/Tenant/UpdateWithWhere`,
        {
          tableName: "ClientDetails",
          updateString: `DELIVERYCHARGES='${newValue}'`,
          whereString: `USERNAME='${record.USERNAME}' AND TENANTNAME='${record.TENANTNAME}' AND FIRMNAME='${record.FIRMNAME}'`,
        },
        { headers: { tenantName } },
      );
      message.success(
        `${record.USERNAME} marked as ${checked ? "Active" : "Inactive"}`,
      );
    } catch (error) {
      console.error("Error updating status:", error);
      message.error("Could not update status");
      setRawData((prev) =>
        prev.map((r) =>
          r.key === rowKey ? { ...r, DELIVERYCHARGES: previousValue } : r,
        ),
      );
    } finally {
      setTogglingKey(null);
    }
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Photo",
      key: "photo",
      width: 64,
      render: (_, record) => {
        const url = getProfilePicUrl(record);
        const src = url ? withCacheBust(url, record.ENDDATE) : undefined;
        return (
          <Avatar
            size={34}
            src={src}
            icon={<UserOutlined />}
            style={{ backgroundColor: "#eef0fb", color: "#0f2460" }}
            onError={() => {
              if (url) console.warn("Profile picture failed to load:", url);
              return true;
            }}
          />
        );
      },
    },
    {
      title: "Username",
      dataIndex: "USERNAME",
      key: "USERNAME",
    },
    {
      title: "Mobile",
      dataIndex: "LOGINMOBILENO",
      key: "LOGINMOBILENO",
    },
    {
      title: "Authenticate Type",
      dataIndex: "ROLE",
      key: "ROLE",
      render: (text) => text?.charAt(0).toUpperCase() + text?.slice(1),
    },
    {
      title: "User Module",
      dataIndex: "USERMODULE",
      key: "USERMODULE",
      render: (text) => text || "—",
    },
    {
      title: "Status",
      dataIndex: "DELIVERYCHARGES",
      key: "status",
      width: 110,
      render: (value, record) => {
        const isActive = String(value) === "1";
        return (
          <Space size={8}>
            <Switch
              checked={isActive}
              loading={togglingKey === record.key}
              onChange={(checked) => handleStatusToggle(record, checked)}
            />
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: isActive ? "#189a4d" : "#c9463c",
              }}
            >
              {isActive ? "Active" : "Inactive"}
            </span>
          </Space>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EditOutlined style={{ color: "#030354" }} />}
            onClick={() => handleEdit(record)}
            style={{
              width: 30,
              height: 30,
              borderRadius: 6,
              backgroundColor: "#eef0fb",
            }}
          />

          <Popconfirm
            title="Delete User"
            description="Are you sure you want to delete this user?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              icon={<DeleteOutlined style={{ color: "#e53935" }} />}
              style={{
                width: 30,
                height: 30,
                borderRadius: 6,
                backgroundColor: "#fdecea",
              }}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const profilePicSrc = profilePic?.preview || existingProfilePicUrl;

  return (
    <div style={G.container}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 24,
          justifyContent: "center",
        }}
      >
        <div style={G.card}>
          <div style={G.header}>{editingKey ? "Edit User" : "Create User"}</div>
          <div style={G.body}>
            <Form
              form={form}
              className="ua-form"
              layout="vertical"
              onFinish={handleSubmit}
              autoComplete="off"
              disabled={resolvingOwner || !ownerRecord}
            >
              {/* ── Profile picture picker ─────────────────────────────── */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-end",
                  gap: 8,
                  marginBottom: 20,
                }}
              >
                <div
                  style={G.avatarPicker}
                  onClick={() => profilePicInputRef.current?.click()}
                >
                  {profilePicSrc ? (
                    <img
                      src={profilePicSrc}
                      alt="Profile"
                      style={G.avatarImg}
                    />
                  ) : (
                    <UserOutlined style={{ fontSize: 30, color: "#9aa4c4" }} />
                  )}
                  <span style={G.avatarBadge}>
                    <CameraOutlined />
                  </span>
                </div>
                {profilePicSrc && (
                  <Tooltip title="Remove photo">
                    <Button
                      type="text"
                      danger
                      size="small"
                      shape="circle"
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearProfilePic();
                      }}
                    />
                  </Tooltip>
                )}
                <input
                  ref={profilePicInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePicSelect}
                  style={{ display: "none" }}
                />
              </div>

              <Form.Item
                label="Username"
                name="username"
                rules={[
                  { required: true, message: "Username is required" },
                  { validator: validateUsername },
                ]}
              >
                <Input
                  ref={usernameRef}
                  id="acct-uname-field"
                  name="acct-uname-field"
                  autoComplete="off"
                  placeholder="Enter username"
                  suffix={usernameSuffix}
                  onChange={(e) => {
                    const val = e.target.value;
                    form.setFieldValue("username", val);
                    checkUsernameAvailability(val);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      mobileRef.current?.focus();
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                label="Mobile Number"
                name="mobile"
                rules={[
                  { required: true, message: "Mobile number is required" },
                  {
                    pattern: /^[0-9]{10}$/,
                    message: "Enter a valid 10-digit mobile number",
                  },
                ]}
              >
                <Input
                  ref={mobileRef}
                  maxLength={10}
                  placeholder="Enter mobile number"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      passwordRef.current?.focus();
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[{ required: true, message: "Password is required" }]}
              >
                <Input.Password
                  ref={passwordRef}
                  autoComplete="new-password"
                  placeholder="Enter password"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      authTypeRef.current?.focus();
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                label="Authenticate Type"
                name="authType"
                rules={[
                  { required: true, message: "Authenticate type is required" },
                ]}
              >
                <AuthTypeAutocomplete
                  inputRef={authTypeRef}
                  onEnterKey={() =>
                    setTimeout(() => userModuleRef.current?.focus(), 0)
                  }
                  onSelect={(val) => {
                    if (val?.toLowerCase() === "administrator") {
                      form.setFieldValue("userModule", "ALL");
                    } else if (val?.toLowerCase() === "operator") {
                      form.setFieldValue("userModule", undefined);
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) => prev.authType !== cur.authType}
              >
                {({ getFieldValue }) => {
                  const isAdmin =
                    getFieldValue("authType")?.toLowerCase() ===
                    "administrator";
                  return (
                    <Form.Item
                      label="User Module"
                      name="userModule"
                      rules={[
                        { required: true, message: "User module is required" },
                      ]}
                      tooltip={
                        isAdmin
                          ? "Administrator always has full access to all modules"
                          : undefined
                      }
                    >
                      <Select
                        ref={userModuleRef}
                        placeholder={
                          isAdmin ? "ALL (auto-assigned)" : "Select user module"
                        }
                        options={
                          isAdmin
                            ? [{ value: "ALL", label: "ALL — Full Access" }]
                            : USER_MODULE_OPTIONS
                        }
                        disabled={isAdmin}
                        style={isAdmin ? { opacity: 0.65 } : undefined}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            setTimeout(() => form.submit(), 0);
                          }
                        }}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>

              <div style={G.actionBar}>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {editingKey ? "Save" : "Create"}
                </Button>
              </div>
            </Form>
          </div>
        </div>
        <div style={G.tableCard}>
          <div style={G.tableHeader}>
            <div
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: "#0f1010",
              }}
            >
              Products List ({filteredData.length})
            </div>

            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search products..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(1);
              }}
              style={{
                width: 320,
              }}
            />

            <Tooltip title="Refresh list">
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading}
              />
            </Tooltip>

            <Pagination
              current={page}
              pageSize={pageSize}
              total={filteredData.length}
              showSizeChanger
              pageSizeOptions={["10", "20", "50", "100"]}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} items`
              }
              onChange={(newPage, newPageSize) => {
                setPage(newPage);
                setPageSize(newPageSize);
              }}
            />
          </div>
          <div style={{ padding: "4px 10px 0px 20px" }}>
            <Table
              columns={columns}
              dataSource={paginatedData}
              rowKey="key"
              pagination={false}
              size="small"
              loading={loading || resolvingOwner}
            />
          </div>
        </div>
      </div>

      {/* Scoped form spacing — keeps labels from sticking to the field above */}
      <style>{`
        .ua-form .ant-form-item {
          margin-bottom: 16px !important;
        }
        .ua-form .ant-form-item-label {
          padding: 0 0 6px !important;
          line-height: 1.5;
        }
        .ua-form .ant-form-item-label > label {
          height: auto !important;
          font-size: 13px;
          font-weight: 600;
          color: #111827;
        }
      fieldset {
  border: none !important;
  display: none !important;
}
.MuiOutlinedInput-root fieldset {
  border: none !important;
  display: none !important;
}
.MuiOutlinedInput-root fieldset {
  border: 1px solid #e5e7eb !important; /* Add a light border */
  border-radius: 8px !important;
  overflow: hidden !important;
}
      `}</style>
    </div>
  );
};

export default UserAccounts;